import React, { useRef, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";

import { CallbackForm } from "./CallbackForm";
import { ProductCard } from "./ProductCard";
import { toneClasses, groupGridClass } from "./tradeTones";
import { setActiveTab } from "../../../../store/activeTabSlice";
import { cn } from "../../../common/u1/cn";
import { CALLBACK_BAND, PRICE_PROMISE } from "../../../../constants/tradeVerticals";
import { CheckIcon, LockIcon } from "../../../../assets/ui/icons";

/* One Trade vertical page — Information, Finance or Risk — built from a config in constants/tradeVerticals.js. */
const EYEBROW = "text-[12px] font-bold uppercase tracking-[1px] font-sans mb-2.5";

export const TradeVerticalPage = ({ config }) => {
  const formRef = useRef(null);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { userInfo } = useSelector((state) => state.auth);
  const isMember = !!userInfo?.is_member;

  /* Drives the top-bar title and the rail's active item, keyed off the nav
     item's name. Transcribed. */
  useEffect(() => {
    dispatch(setActiveTab(config.eyebrow));
  }, [dispatch, config.eyebrow]);

  const tone = toneClasses(config.accent);

  const scrollToForm = () => {
    formRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const handleCardCta = (card) => {
    if (card.ctaAction === "search") navigate("/search");
    else if (card.ctaAction === "membership") navigate("/plans");
    else scrollToForm();
  };

  return (
    <div className="u1-root mt-6 max-w-[1120px] text-left text-[var(--navy)]">
      {/* ── Hero ── */}
      <div className="border-b border-[var(--trade-rule)] pb-7">
        <div className={cn(EYEBROW, tone.eyebrow)}>{config.eyebrow}</div>
        <h1 className="m-0 max-w-[680px] text-[34px] font-bold leading-[1.12] tracking-[-.6px] text-[var(--navy)] font-sans">
          {config.headline}
        </h1>
        <p className="mt-3.5 mb-0 max-w-[660px] text-[15.5px] leading-[1.6] text-[var(--trade-body)] font-sans">
          {config.sub}
        </p>
        <div className="mt-[22px] flex flex-wrap items-center gap-4">
          <button
            type="button"
            onClick={scrollToForm}
            className="cursor-pointer rounded-[10px] bg-[var(--orange)] px-6 py-[13px] text-[14.5px] font-bold text-white font-sans transition-colors hover:bg-[var(--orange-hover)]"
          >
            Talk to our team
          </button>
          <span className="max-w-[330px] text-[12.5px] leading-[1.45] text-[var(--trade-micro)] font-sans">
            Tell us what you need. A member of the TREVEX team calls you back, usually the same
            working day.
          </span>
        </div>
      </div>

      {/* ── Product groups ── */}
      {config.groups.map((group) => (
        <section key={group.title} className="mt-[38px]">
          <div className={cn(EYEBROW, tone.eyebrow)}>{group.kicker}</div>
          <h2 className="m-0 mb-1 text-[21px] font-bold tracking-[-.2px] text-[var(--navy)] font-sans">
            {group.title}
          </h2>
          {group.intro && (
            <p className="mt-2 mb-0 max-w-[640px] text-[14px] leading-[1.55] text-[var(--trade-intro)] font-sans">
              {group.intro}
            </p>
          )}

          <div className={cn("mt-[18px] grid gap-[18px]", groupGridClass(group.cards))}>
            {group.cards.map((card) => (
              <ProductCard
                key={card.title}
                card={card}
                accent={config.accent}
                onCta={handleCardCta}
              />
            ))}
          </div>
        </section>
      ))}

      {/* ── Explanatory note ── */}
      {config.note && (
        <div className="mt-[34px] rounded-xl border border-[var(--card-line)] border-l-[3px] border-l-[var(--orange)] bg-white px-6 py-[22px]">
          <h3 className="m-0 mb-[7px] text-[15.5px] font-bold text-[var(--navy)] font-sans">
            {config.note.title}
          </h3>
          <p className="m-0 max-w-[780px] text-[13.5px] leading-[1.6] text-[var(--trade-body)] font-sans">
            {config.note.body}
          </p>
        </div>
      )}

      {/* ── Non-member upsell ──
          NOTE: the "Become a Member" CTA is intentionally omitted until the
          TIP-489 membership page exists — membership purchase is company-gated.
          Carried over from the live page unchanged. */}
      {config.nonMember && !isMember && (
        <div className="mt-[34px] flex flex-wrap items-center justify-between gap-[22px] rounded-[14px] bg-[var(--navy)] px-7 py-[26px]">
          <div className="min-w-[240px] grow basis-0">
            <h3 className="m-0 mb-[7px] text-[18px] font-bold text-white font-sans">
              {config.nonMember.heading}
            </h3>
            <p className="m-0 max-w-[620px] text-[13.5px] leading-[1.6] text-[var(--panel-body)] font-sans">
              {config.nonMember.body}
            </p>
          </div>
        </div>
      )}

      {/* ── Callback band ── */}
      <div
        ref={formRef}
        className="mt-[34px] scroll-mt-20 rounded-2xl border border-[var(--card-feature-line)] bg-[#f5f1e8] p-7"
      >
        <div className="grid grid-cols-1 items-start gap-8 md:grid-cols-2">
          <div>
            <h3 className="m-0 mb-2 text-[20px] font-bold text-[var(--navy)] font-sans">
              {CALLBACK_BAND.heading}
            </h3>
            <p className="mt-0 mb-3.5 text-[14px] leading-[1.6] text-[var(--trade-body)] font-sans">
              {CALLBACK_BAND.body}
            </p>
            <div className="flex items-start gap-[9px]">
              <span className="mt-0.5 shrink-0 text-[var(--tone-green)]">
                <CheckIcon size={16} stroke="currentColor" />
              </span>
              <span className="text-[12.5px] leading-[1.5] text-[var(--trade-intro)] font-sans">
                {CALLBACK_BAND.reassurance}
              </span>
            </div>
          </div>

          <CallbackForm
            product={config.product}
            options={config.callbackOptions}
            isMember={isMember}
          />
        </div>
      </div>

      {/* ── Price promise ── */}
      <div className="mt-[26px] flex items-start gap-[11px] px-0.5 py-1">
        <span className="mt-px shrink-0 text-[var(--trade-micro)]">
          <LockIcon size={17} />
        </span>
        <p className="m-0 max-w-[820px] text-[12.5px] leading-[1.6] text-[var(--trade-micro)] font-sans">
          {PRICE_PROMISE}
        </p>
      </div>
    </div>
  );
};

export default TradeVerticalPage;
